/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useFirewallSettings.ts
 🗓️ Created: 2025-12-06
 🧩 Description:
    Hook for managing Firewall Settings
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import {
  fetchFirewallSettings,
  updateFirewallSettings,
  getServiceStatuses,
  controlFirewallService,
  controlFail2BanService,
} from '../api/firewallSettingsApi';
import type { FirewallSettings, FirewallSettingsFormData } from '../types';

export function useFirewallSettings() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const [settings, setSettings] = useState<FirewallSettings | null>(null);
  const [serviceStatuses, setServiceStatuses] = useState<{
    firewallEnabled: boolean;
    intrusionDetectionEnabled: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsData, serviceData] = await Promise.all([
        fetchFirewallSettings(),
        getServiceStatuses(),
      ]);
      setSettings(settingsData);
      setServiceStatuses(serviceData);
    } catch (error) {
      showError(error.message || t('firewallSettings.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  const loadServiceStatuses = useCallback(async () => {
    try {
      const serviceData = await getServiceStatuses();
      setServiceStatuses(serviceData);
    } catch (error) {
      showError(error.message || 'Failed to load service statuses');
    }
  }, [showError]);

  const saveSettings = useCallback(async (data: FirewallSettingsFormData) => {
    try {
      setSaving(true);
      const updated = await updateFirewallSettings(data);
      setSettings(updated);
      showSuccess(t('firewallSettings.messages.saveSuccess'));
      return updated;
    } catch (error) {
      showError(error.message || t('firewallSettings.messages.saveError'));
      throw error;
    } finally {
      setSaving(false);
    }
  }, [showSuccess, showError, t]);

  const toggleFirewall = useCallback(async (enabled: boolean) => {
    try {
      setSaving(true);
      const action = enabled ? 'start' : 'stop';
      await controlFirewallService(action);
      await loadServiceStatuses();
      showSuccess(enabled ? 'Firewall enabled' : 'Firewall disabled');
    } catch (error) {
      showError(error.message || 'Failed to toggle firewall');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [loadServiceStatuses, showSuccess, showError]);

  const toggleIntrusionDetection = useCallback(async (enabled: boolean) => {
    try {
      setSaving(true);
      const action = enabled ? 'start' : 'stop';
      await controlFail2BanService(action);
      await loadServiceStatuses();
      showSuccess(enabled ? 'Intrusion detection enabled' : 'Intrusion detection disabled');
    } catch (error) {
      showError(error.message || 'Failed to toggle intrusion detection');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [loadServiceStatuses, showSuccess, showError]);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return {
    settings,
    serviceStatuses,
    loading,
    saving,
    loadSettings,
    saveSettings,
    toggleFirewall,
    toggleIntrusionDetection,
    loadServiceStatuses,
  };
}
